import {
  PermissionResult,
  PermissionCheck,
  Resource,
  PermissionAction,
  AttributeContext,
  PermissionAuditEvent,
  AuditFilter,
  Role,
  BulkPermissionChange,
  PermissionScope,
} from "../../types/permissions";
import { logger } from "../../utils/logger";
import { config } from "@/config";
import { getCurrentAccessToken } from "@/services/api/auth-session";

// API Configuration
const API_BASE_URL = config.api.baseUrl;
const PERMISSIONS_ENDPOINT = `${API_BASE_URL}/permissions`;

// Request/Response Types
interface CheckPermissionRequest {
  resource: Resource;
  action: PermissionAction;
  context?: Partial<AttributeContext>;
  userId: string;
}

interface CheckBulkPermissionsRequest {
  checks: PermissionCheck[];
  userId: string;
}

interface CheckBulkPermissionsResponse {
  results: Record<string, PermissionResult>;
  cached: Record<string, boolean>;
  errors: Record<string, string>;
}

interface GetRolesRequest {
  scope: PermissionScope;
  resourceId?: string;
  userId?: string;
}

interface UpdateRolePermissionsRequest {
  roleId: string;
  changes: BulkPermissionChange[];
  reason?: string;
}

interface GetAuditEventsRequest {
  filter: AuditFilter;
  page?: number;
  limit?: number;
}

interface GetAuditEventsResponse {
  events: PermissionAuditEvent[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface PermissionRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

class PermissionService {
  private cache = new Map<
    string,
    { result: PermissionResult; timestamp: number }
  >();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  // HTTP helper with authentication
  private async request<T>(
    endpoint: string,
    options: PermissionRequestOptions = {},
  ): Promise<T> {
    const token = getCurrentAccessToken();

    const response = await fetch(`${PERMISSIONS_ENDPOINT}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData: unknown = JSON.parse(errorText);
        if (errorData != null && typeof errorData === 'object' && 'message' in errorData && typeof (errorData as Record<string, unknown>).message === 'string') {
          errorMessage = (errorData as Record<string, unknown>).message as string;
        }
      } catch {
        // Use default error message if parsing fails
      }

      logger.error("Permission service request failed", {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        error: errorMessage,
      });

      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Generate cache key for permission checks
  private getCacheKey(
    resource: Resource,
    action: PermissionAction,
    userId: string,
  ): string {
    return `${userId}:${resource.type}:${resource.id}:${action}`;
  }

  // Check if cached result is still valid
  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.cacheTimeout;
  }

  private getBulkResultKey(check: PermissionCheck): string {
    return `${check.resource.type}-${check.resource.id}-${check.action}`;
  }

  // Single permission check
  async checkPermission(
    request: CheckPermissionRequest,
  ): Promise<PermissionResult> {
    const cacheKey = this.getCacheKey(
      request.resource,
      request.action,
      request.userId,
    );

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && this.isCacheValid(cached.timestamp)) {
      logger.debug("Permission cache hit", {
        cacheKey,
        resource: request.resource.type,
        action: request.action,
      });
      return { ...cached.result, cached: true };
    }

    try {
      logger.debug("Checking permission", {
        resource: request.resource,
        action: request.action,
        userId: request.userId,
      });

      const result = await this.request<PermissionResult>("/check", {
        method: "POST",
        body: JSON.stringify(request),
      });

      // Cache the result
      this.cache.set(cacheKey, {
        result: { ...result, cached: false },
        timestamp: Date.now(),
      });

      logger.debug("Permission checked", {
        resource: request.resource.type,
        action: request.action,
        allowed: result.allowed,
        reason: result.reason,
      });

      return result;
    } catch (error) {
      logger.error("Permission check failed", {
        resource: request.resource,
        action: request.action,
        userId: request.userId,
        error,
      });

      // Return deny by default on error
      const errorResult: PermissionResult = {
        allowed: false,
        reason:
          error instanceof Error ? error.message : "Permission check failed",
        evaluatedAt: new Date(),
        cached: false,
      };

      return errorResult;
    }
  }

  // Bulk permission check
  async checkBulkPermissions(
    request: CheckBulkPermissionsRequest,
  ): Promise<Record<string, PermissionResult>> {
    // Separate cached and uncached checks
    const uncachedChecks: PermissionCheck[] = [];
    const results: Record<string, PermissionResult> = {};

    for (const check of request.checks) {
      const cacheKey = this.getCacheKey(
        check.resource,
        check.action,
        request.userId,
      );
      const cached = this.cache.get(cacheKey);
      const resultKey = this.getBulkResultKey(check);

      if (cached && this.isCacheValid(cached.timestamp)) {
        results[resultKey] = { ...cached.result, cached: true };
        logger.debug("Bulk permission cache hit", {
          checkId: resultKey,
          cacheKey,
        });
      } else {
        uncachedChecks.push(check);
      }
    }

    // If all checks were cached, return results
    if (uncachedChecks.length === 0) {
      logger.debug("All bulk permissions served from cache", {
        total: request.checks.length,
      });
      return results;
    }

    try {
      logger.debug("Checking bulk permissions", {
        total: request.checks.length,
        cached: Object.keys(results).length,
        uncached: uncachedChecks.length,
      });

      const response = await this.request<CheckBulkPermissionsResponse>(
        "/check-bulk",
        {
          method: "POST",
          body: JSON.stringify({
            checks: uncachedChecks,
            userId: request.userId,
          }),
        },
      );

      // Process results and update cache
      Object.entries(response.results).forEach(([checkId, result]) => {
        results[checkId] = result;

        // Find the check to get resource/action for caching
        const check = uncachedChecks.find((c) => this.getBulkResultKey(c) === checkId);
        if (check) {
          const cacheKey = this.getCacheKey(
            check.resource,
            check.action,
            request.userId,
          );
          this.cache.set(cacheKey, {
            result: { ...result, cached: false },
            timestamp: Date.now(),
          });
        }
      });

      // Handle any errors by returning deny results
      Object.entries(response.errors || {}).forEach(([checkId, error]) => {
        if (!results[checkId]) {
          results[checkId] = {
            allowed: false,
            reason: error,
            evaluatedAt: new Date(),
            cached: false,
          };
        }
      });

      logger.debug("Bulk permissions checked", {
        total: request.checks.length,
        successful: Object.keys(response.results).length,
        errors: Object.keys(response.errors || {}).length,
      });

      return results;
    } catch (error) {
      logger.error("Bulk permission check failed", {
        checksCount: request.checks.length,
        error,
      });

      // Return deny results for any unchecked permissions
      uncachedChecks.forEach((check) => {
        const resultKey = this.getBulkResultKey(check);
        if (!results[resultKey]) {
          results[resultKey] = {
            allowed: false,
            reason:
              error instanceof Error
                ? error.message
                : "Bulk permission check failed",
            evaluatedAt: new Date(),
            cached: false,
          };
        }
      });

      return results;
    }
  }

  // Get roles for a scope
  async getRoles(request: GetRolesRequest): Promise<Role[]> {
    try {
      logger.debug("Fetching roles", {
        scope: request.scope,
        resourceId: request.resourceId,
      });

      const queryParams = new URLSearchParams({
        scope: request.scope,
        ...(request.resourceId && { resourceId: request.resourceId }),
        ...(request.userId && { userId: request.userId }),
      });

      return await this.request<Role[]>(`/roles?${queryParams}`);
    } catch (error) {
      logger.error("Failed to fetch roles", { request, error });
      return [];
    }
  }

  // Update role permissions
  async updateRolePermissions(
    request: UpdateRolePermissionsRequest,
  ): Promise<void> {
    try {
      logger.debug("Updating role permissions", {
        roleId: request.roleId,
        changesCount: request.changes.length,
      });

      await this.request("/roles/permissions", {
        method: "PUT",
        body: JSON.stringify(request),
      });

      // Invalidate relevant cache entries
      this.invalidateRoleCache(request.roleId);

      logger.info("Role permissions updated", {
        roleId: request.roleId,
        changes: request.changes.length,
      });
    } catch (error) {
      logger.error("Failed to update role permissions", { request, error });
      throw error;
    }
  }

  // Get audit events
  async getAuditEvents(
    request: GetAuditEventsRequest,
  ): Promise<GetAuditEventsResponse> {
    try {
      logger.debug("Fetching audit events", { filter: request.filter });

      const response = await this.request<GetAuditEventsResponse>("/audit", {
        method: "POST",
        body: JSON.stringify(request),
      });

      logger.debug("Audit events fetched", {
        count: response.events.length,
        total: response.total,
      });

      return response;
    } catch (error) {
      logger.error("Failed to fetch audit events", { request, error });
      return {
        events: [],
        total: 0,
        page: request.page || 1,
        limit: request.limit || 50,
        hasMore: false,
      };
    }
  }

  // Invalidate cache for specific patterns
  invalidateCache(pattern?: string): void {
    if (!pattern) {
      // Clear all cache
      this.cache.clear();
      logger.debug("Permission cache cleared");
      return;
    }

    // Remove matching entries
    const keysToDelete = Array.from(this.cache.keys()).filter((key) =>
      key.includes(pattern),
    );
    keysToDelete.forEach((key) => this.cache.delete(key));

    logger.debug("Permission cache invalidated", {
      pattern,
      removed: keysToDelete.length,
    });
  }

  // Invalidate cache for a specific role
  private invalidateRoleCache(roleId: string): void {
    // This is a simplified approach - in a real system you'd need to track
    // which cache entries are affected by role changes
    this.cache.clear();
    logger.debug("Cache invalidated for role change", { roleId });
  }

  // Get cache statistics
  getCacheStats(): { size: number; hitRate: number } {
    // Simple cache stats - in production you'd track hits/misses
    return {
      size: this.cache.size,
      hitRate: 0, // Placeholder - would need hit/miss tracking
    };
  }

  // Preload permissions for resources
  async preloadPermissions(
    resources: Resource[],
    actions: PermissionAction[],
    userId: string,
  ): Promise<void> {
    const checks: PermissionCheck[] = [];

    resources.forEach((resource) => {
      actions.forEach((action) => {
        const cacheKey = this.getCacheKey(resource, action, userId);

        // Only preload if not already cached
        if (!this.cache.has(cacheKey)) {
          checks.push({
            resource,
            action,
            userId,
          });
        }
      });
    });

    if (checks.length > 0) {
      logger.debug("Preloading permissions", { count: checks.length });

      try {
        await this.checkBulkPermissions({ checks, userId });
        logger.debug("Permissions preloaded", { count: checks.length });
      } catch (error) {
        logger.warn("Permission preload failed", { error });
      }
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.request("/health");
      return true;
    } catch (error) {
      logger.warn("Permission service health check failed", { error });
      return false;
    }
  }
}

// Export singleton instance
export const permissionService = new PermissionService();
export default permissionService;
